import { useCallback, useMemo } from 'react'
import { collection, query, serverTimestamp, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { fbt } from 'fbtee'
import { addDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { useFirestoreCollection } from './useFirestore'
import type { ScheduleLead } from '../types'

// A user telling us an event's set times are out. Clients only ever create —
// rules reject any write past that, and triage happens in the admin queue.
export function useScheduleLeads(eventId: string, userId: string | null) {
  // Scoped to the viewer's own leads so a repeat visit can say "we're on it"
  // instead of offering the form again. Rules allow reading your own.
  const myLeadsQuery = useMemo(
    () =>
      userId
        ? (query(
            collection(db, 'scheduleLeads'),
            where('submittedBy', '==', userId),
            where('eventId', '==', eventId),
          ) as Query<ScheduleLead>)
        : null,
    [userId, eventId],
  )

  const { data } = useFirestoreCollection<ScheduleLead>(myLeadsQuery)

  const alreadyReported = useMemo(() => data.some(l => l.status === 'pending'), [data])

  const submitLead = useCallback(
    async (url: string) => {
      if (!userId) return
      const trimmed = url.trim()
      await runMutation(
        fbt('Could not send your report', 'Error toast when reporting set times fails'),
        async () => {
          await addDoc(collection(db, 'scheduleLeads'), {
            eventId,
            ...(trimmed ? { url: trimmed } : {}),
            submittedBy: userId,
            createdAt: serverTimestamp(),
            status: 'pending',
          })
          track('schedule_lead_submitted', { event_id: eventId, has_link: Boolean(trimmed) })
        },
      )
    },
    [userId, eventId],
  )

  return { submitLead, alreadyReported }
}
